import { ErrorOutline, OpenInNew } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Link,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

export type AsyncJobFailedItem = {
  id: string;
  primaryText: string;
  secondaryText?: string;
  reason?: string;
  destination: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  actionLabel: string;
  failedCount: number;
  items: AsyncJobFailedItem[];
  isLoading: boolean;
  isError: boolean;
};

export const AsyncJobFailedItemsModal: FC<Props> = ({
  isOpen,
  onClose,
  title,
  description,
  actionLabel,
  failedCount,
  items,
  isLoading,
  isError,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const navigateToItem = (destination: string): void => {
    onClose();
    navigate(destination);
  };

  return (
    <Modal
      closeAfterTransition
      open={isOpen}
      onClose={onClose}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={isOpen} timeout={300}>
        <Box
          sx={{
            outline: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 2,
          }}
          onClick={onClose}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: 1,
              maxWidth: 560,
              maxHeight: '80vh',
              borderRadius: 2.5,
              backgroundColor: theme.palette.common.white,
              boxShadow: 24,
            }}
            onClick={(event): void => event.stopPropagation()}
          >
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <ErrorOutline sx={{ fontSize: 28, color: theme.palette.SlabRed[500] }} />
                <Typography variant='h5' fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {description}
              </Typography>
            </Box>

            <Divider />

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 3,
                py: 2,
              }}
            >
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {!isLoading && !isError && items.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {items.map((item, index) => (
                    <Box key={item.id}>
                      <Box
                        sx={{
                          py: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Link
                              component='button'
                              underline='hover'
                              variant='body2'
                              fontWeight={600}
                              sx={{ textAlign: 'left' }}
                              onClick={(): void => navigateToItem(item.destination)}
                            >
                              {item.primaryText}
                            </Link>
                            {item.secondaryText !== undefined && (
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {item.secondaryText}
                              </Typography>
                            )}
                          </Box>

                          <Link
                            component='button'
                            underline='hover'
                            variant='body2'
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              whiteSpace: 'nowrap',
                              ml: 2,
                            }}
                            onClick={(): void => navigateToItem(item.destination)}
                          >
                            {actionLabel}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </Box>

                        {item.reason !== undefined && (
                          <Box
                            sx={{
                              mt: 1,
                              borderRadius: 1,
                              backgroundColor: theme.palette.SlabRed[100],
                              color: theme.palette.SlabRed[600],
                              px: 1.25,
                              py: 0.75,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                              {item.reason}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {index < items.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Box>
              )}

              {!isLoading && (isError || (failedCount > 0 && items.length === 0)) && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  Unable to load failure details.
                </Typography>
              )}

              {!isLoading && !isError && failedCount === 0 && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  No failures to display.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                px: 3,
                py: 2,
              }}
            >
              <Button variant='slab-outline' sx={{ flex: 1 }} onClick={onClose}>
                CLOSE
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
